import { Injectable } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { AuthService } from './auth.service';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

export interface UsageLimit {
  featureType: string;
  usageDate: string;
  usageCount: number;
  remaining: number;
}

export interface UsageLimitResult {
  canUse: boolean;
  remaining: number;
  resetTime?: Date;
  message?: string;
}

/**
 * Service for tracking and enforcing daily usage limits on AI features
 */
@Injectable({
  providedIn: 'root',
})
export class UsageLimitService {
  constructor(
    private supabaseService: SupabaseService,
    private authService: AuthService
  ) {}

  /**
   * Check if user can use a feature based on daily limit
   * @param featureType - Type of feature (e.g., 'resume_analysis')
   * @param limit - Maximum daily usage allowed
   * @returns Observable with usage limit result
   */
  checkDailyLimit(
    featureType: string,
    limit: number
  ): Observable<UsageLimitResult> {
    const userId = this.authService.getCurrentUser()?.id;
    
    if (!userId) {
      return throwError(() => new Error('User must be authenticated'));
    }

    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

    return from(
      this.supabaseService
        .getClient()
        .from('ai_usage_tracking')
        .select('*')
        .eq('user_id', userId)
        .eq('feature_type', featureType)
        .eq('usage_date', today)
        .maybeSingle()
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          throw error;
        }

        // If no record exists, user hasn't used the feature today
        if (!data) {
          return {
            canUse: true,
            remaining: limit,
          };
        }

        const usageCount = data.usage_count || 0;
        const remaining = Math.max(0, limit - usageCount);
        const canUse = remaining > 0;

        // Calculate reset time (midnight tonight)
        const resetTime = new Date();
        resetTime.setHours(24, 0, 0, 0);

        return {
          canUse,
          remaining,
          resetTime,
          message: canUse
            ? `${remaining} of ${limit} analyses remaining today`
            : `Daily limit reached. Resets at midnight.`,
        };
      }),
      catchError((error) => {
        console.error('Error checking daily limit:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Increment usage count for a feature
   * @param featureType - Type of feature (e.g., 'resume_analysis')
   * @returns Observable with updated usage count
   */
  incrementUsage(featureType: string): Observable<void> {
    const userId = this.authService.getCurrentUser()?.id;
    
    if (!userId) {
      return throwError(() => new Error('User must be authenticated'));
    }

    const today = new Date().toISOString().split('T')[0];

    return from(
      this.supabaseService
        .getClient()
        .from('ai_usage_tracking')
        .select('*')
        .eq('user_id', userId)
        .eq('feature_type', featureType)
        .eq('usage_date', today)
        .maybeSingle()
    ).pipe(
      switchMap(({ data, error }) => {
        if (error) {
          throw error;
        }

        // If no record exists, create one
        if (!data) {
          return from(
            this.supabaseService
              .getClient()
              .from('ai_usage_tracking')
              .insert({
                user_id: userId,
                feature_type: featureType,
                usage_date: today,
                usage_count: 1,
              })
          );
        }

        // Update existing record
        return from(
          this.supabaseService
            .getClient()
            .from('ai_usage_tracking')
            .update({
              usage_count: (data.usage_count || 0) + 1,
              updated_at: new Date().toISOString(),
            })
            .eq('id', data.id)
        );
      }),
      map((result: any) => {
        if (result.error) {
          throw result.error;
        }
        return void 0;
      }),
      catchError((error) => {
        console.error('Error incrementing usage:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get remaining usage for a feature
   * @param featureType - Type of feature (e.g., 'resume_analysis')
   * @param limit - Maximum daily usage allowed
   * @returns Observable with remaining count
   */
  getRemainingUsage(featureType: string, limit: number): Observable<number> {
    return this.checkDailyLimit(featureType, limit).pipe(
      map((result) => result.remaining)
    );
  }

  /**
   * Get usage history for a feature
   * @param featureType - Type of feature (e.g., 'resume_analysis')
   * @param days - Number of days to look back (default 7)
   * @returns Observable with usage history
   */
  getUsageHistory(
    featureType: string,
    days: number = 7
  ): Observable<UsageLimit[]> {
    const userId = this.authService.getCurrentUser()?.id;
    
    if (!userId) {
      return throwError(() => new Error('User must be authenticated'));
    }

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    const startDateStr = startDate.toISOString().split('T')[0];

    return from(
      this.supabaseService
        .getClient()
        .from('ai_usage_tracking')
        .select('*')
        .eq('user_id', userId)
        .eq('feature_type', featureType)
        .gte('usage_date', startDateStr)
        .order('usage_date', { ascending: false })
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          throw error;
        }

        return (data || []).map((record: any) => ({
          featureType: record.feature_type,
          usageDate: record.usage_date,
          usageCount: record.usage_count,
          remaining: 0, // Can be calculated if needed
        }));
      }),
      catchError((error) => {
        console.error('Error fetching usage history:', error);
        return throwError(() => error);
      })
    );
  }
}

